import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-4 bg-brown-800" data-id="element-45">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-46">
          <p className="text-cream-200/60 text-sm" data-id="element-47">
            © {new Date().getFullYear()} Mari Algarra. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-48">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-cream-200/60 hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-49">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-cream-200/60 hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-50">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-51">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-52" />}
      </AnimatePresence>
    </>;
}
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-53">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-brown-800/60 backdrop-blur-sm" data-id="element-54" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden z-10" data-id="element-55">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-cream-200" data-id="element-56">
          <h2 className="text-2xl font-serif text-brown-800" data-id="element-57">{title}</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-brown-800 transition-colors p-1 rounded-full hover:bg-cream-100" aria-label="Cerrar" data-id="element-58">
            <XIcon className="w-5 h-5" data-id="element-59" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-stone-600 leading-relaxed space-y-4 text-sm" data-id="element-60">
          {content.map((section, i) => <div key={i} data-id="element-61">
              {section.heading && <h3 className="font-bold text-brown-800 text-base mt-4 mb-2" data-id="element-62">
                  {section.heading}
                </h3>}
              <p data-id="element-63">{section.text}</p>
            </div>)}
        </div>
      </motion.div>
    </motion.div>;
}
const privacyContent = [{
  heading: '',
  text: 'Última actualización: junio 2025'
}, {
  heading: '1. Información que recopilamos',
  text: 'Al adquirir nuestros productos digitales, recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados de forma segura por nuestro proveedor de pagos.'
}, {
  heading: '2. Uso de la información',
  text: 'Utilizamos tu información para: entregar el producto digital adquirido, enviarte confirmación de compra, comunicarnos contigo sobre actualizaciones relevantes del producto, y mejorar nuestros servicios.'
}, {
  heading: '3. Protección de datos',
  text: 'Implementamos medidas de seguridad razonables para proteger tu información personal. Sin embargo, ningún método de transmisión por Internet es 100% seguro.'
}, {
  heading: '4. Compartir información',
  text: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto proveedores de servicios necesarios para operar nuestro negocio (procesadores de pago, plataformas de entrega de productos).'
}, {
  heading: '5. Cookies',
  text: 'Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.'
}, {
  heading: '6. Tus derechos',
  text: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales. Para ejercer estos derechos, contáctanos a través de nuestras redes sociales.'
}, {
  heading: '7. Cambios a esta política',
  text: 'Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.'
}];
const termsContent = [{
  heading: '',
  text: 'Última actualización: junio 2025'
}, {
  heading: '1. Aceptación de los términos',
  text: 'Al comprar y/o descargar cualquier producto digital de Mari Algarra ("Plan 14 Días" u otros), aceptas estos términos y condiciones en su totalidad.'
}, {
  heading: '2. Naturaleza del producto',
  text: 'Nuestros productos son materiales educativos en formato digital (PDF). No constituyen asesoría financiera, legal, profesional ni garantía de resultados. Los resultados dependen de la ejecución individual de cada persona.'
}, {
  heading: '3. Entrega del producto',
  text: 'Los productos digitales se entregan de forma inmediata tras la confirmación del pago. Recibirás acceso al archivo descargable a través del correo electrónico proporcionado durante la compra.'
}, {
  heading: '4. Política de reembolso',
  text: 'Debido a la naturaleza digital e inmediatamente descargable de nuestros productos, no ofrecemos reembolsos una vez realizada la descarga. Si tienes problemas técnicos con la descarga, contáctanos y te ayudaremos.'
}, {
  heading: '5. Propiedad intelectual',
  text: 'Todo el contenido de nuestros productos digitales está protegido por derechos de autor. La compra te otorga una licencia personal e intransferible. No está permitido reproducir, distribuir, revender ni compartir el contenido sin autorización expresa.'
}, {
  heading: '6. Uso permitido',
  text: 'El producto es para tu uso personal. Puedes imprimir las plantillas y checklists para tu uso propio. No puedes utilizar el contenido para crear productos derivados para venta.'
}, {
  heading: '7. Limitación de responsabilidad',
  text: 'Mari Algarra no se hace responsable de decisiones tomadas basándose en el contenido de los productos digitales. El uso del material es bajo tu propia responsabilidad.'
}, {
  heading: '8. Modificaciones',
  text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación en esta página.'
}];